/**
 * Zod šeme za forme (web i mobilna). Poruke grešaka su i18n ključevi u namespaceu `errors`
 * (npr. `t('errors:' + issue.message)`), a server ponavlja istu validaciju i vraća `validation_failed` + `field`.
 */
import { z } from 'zod';

import {
  BUILDING_TYPES,
  CLEARANCE_RULE_STATUSES,
  DECISIONS,
  OPENING_TYPE_KEYS,
  PASSWORD_MIN_LENGTH,
  PHOTO_CATEGORIES,
  FEATURE_KEYS,
  FEATURE_STATES,
  ROLES,
} from './domain';
import { checkPib, checkRegistration, checkVat, COUNTRIES } from './taxIds';

export const V = {
  required: 'validation.required',
  email: 'validation.email',
  passwordMin: 'validation.passwordMin',
  passwordMismatch: 'validation.passwordMismatch',
  tooShort: 'validation.tooShort',
  tooLong: 'validation.tooLong',
  positiveInt: 'validation.positiveInt',
  nonNegativeInt: 'validation.nonNegativeInt',
  outOfRange: 'validation.outOfRange',
  jib: 'validation.jib',
  phone: 'validation.phone',
  date: 'validation.date',
  consent: 'validation.consent',
  terms: 'validation.terms',
  invalidOption: 'validation.invalidOption',
  noteRequired: 'validation.noteRequired',
  signatureRequired: 'validation.signatureRequired',
  totp: 'validation.totp',
} as const;

const trimmed = (min: number, max: number) =>
  z
    .string(V.required)
    .trim()
    .min(min, min <= 1 ? V.required : V.tooShort)
    .max(max, V.tooLong);
const optionalText = (max: number) => z.string().trim().max(max, V.tooLong).optional();
const email = z.string(V.required).trim().toLowerCase().max(254, V.tooLong).pipe(z.email(V.email));
const password = z.string(V.required).min(PASSWORD_MIN_LENGTH, V.passwordMin).max(256, V.tooLong);
const jib = z
  .string()
  .trim()
  .regex(/^[0-9 .\-/]{8,17}$/, V.jib)
  .optional()
  .or(z.literal('').transform(() => undefined));
const taxText = z.string().trim().max(20, V.tooLong).optional();
const country = z.enum(COUNTRIES, V.invalidOption);

/**
 * JIB/MB, PIB i PDV broj po državi (isto kao server, `internal/taxid`); greška ide na polje
 * (`errors:validation.<kod>`, npr. `validation.pibChecksum`).
 */
function refineTaxIds(
  v: { country?: string; jib?: string; pib?: string; vatNumber?: string },
  ctx: z.RefinementCtx,
): void {
  const c = v.country ?? 'BA';
  const reg = checkRegistration(c, v.jib);
  if (reg.code) ctx.addIssue({ code: 'custom', path: ['jib'], message: `validation.${reg.code}` });
  const pib = checkPib(c, v.pib);
  if (pib.code) ctx.addIssue({ code: 'custom', path: ['pib'], message: `validation.${pib.code}` });
  const vat = checkVat(v.vatNumber);
  if (vat.code) {
    ctx.addIssue({ code: 'custom', path: ['vatNumber'], message: `validation.${vat.code}` });
  }
}
const phone = z
  .string()
  .trim()
  .max(40, V.tooLong)
  .regex(/^[0-9+()\s/-]*$/, V.phone)
  .optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, V.date);

/** Mjera u mm: pozitivan cijeli broj ili null (prazno polje). */
export const mmSchema = (max = 20000) =>
  z
    .number(V.positiveInt)
    .int(V.positiveInt)
    .min(1, V.positiveInt)
    .max(max, V.outOfRange)
    .nullable();
/** Mjera u mm koja smije biti 0 (špalete, zid, parapet). */
export const mm0Schema = (max = 2000) =>
  z
    .number(V.nonNegativeInt)
    .int(V.nonNegativeInt)
    .min(0, V.nonNegativeInt)
    .max(max, V.outOfRange)
    .nullable();

// ---------------------------------------------------------------- auth
export const loginSchema = z.object({
  email,
  password: z.string(V.required).min(1, V.required).max(256, V.tooLong),
  client: z.enum(['web', 'mobile']).default('web'),
  deviceName: optionalText(120),
});
export type LoginInput = z.input<typeof loginSchema>;

export const registerCompanySchema = z
  .object({
    companyName: trimmed(2, 160),
    jib,
    country: country.optional(),
    pib: taxText,
    vatNumber: taxText,
    name: trimmed(2, 120),
    email,
    password,
    language: z.enum(['sr', 'en', 'de'], V.invalidOption),
    acceptTerms: z.literal(true, V.terms),
    honeypot: z.string().max(200).optional(),
    captchaToken: z.string().optional(),
  })
  .superRefine(refineTaxIds);
export type RegisterCompanyInput = z.input<typeof registerCompanySchema>;

export const forgotPasswordSchema = z.object({ email });

export const resetPasswordSchema = z
  .object({
    token: z.string().min(16, V.required),
    password,
    passwordConfirm: z.string(V.required),
  })
  .refine((v) => v.password === v.passwordConfirm, {
    path: ['passwordConfirm'],
    message: V.passwordMismatch,
  });

export const acceptInviteSchema = z.object({
  token: z.string().min(16, V.required),
  name: trimmed(2, 120),
  password,
});

export const changePasswordSchema = z
  .object({
    currentPassword: z.string(V.required).min(1, V.required),
    newPassword: password,
    newPasswordConfirm: z.string(V.required),
  })
  .refine((v) => v.newPassword === v.newPasswordConfirm, {
    path: ['newPasswordConfirm'],
    message: V.passwordMismatch,
  });

// ---------------------------------------------------------------- settings / users
export const organizationSchema = z
  .object({
    name: trimmed(2, 160),
    subtitle: optionalText(160),
    country,
    jib,
    pib: taxText,
    vatNumber: taxText,
    address: optionalText(200),
    city: optionalText(100),
    phone,
    email: email.optional().or(z.literal('').transform(() => undefined)),
    documentLanguage: z.enum(['sr', 'en']),
    projectNumberPattern: z
      .string()
      .trim()
      .min(6, V.tooShort)
      .max(40, V.tooLong)
      .regex(/\{N{2,8}\}/, V.invalidOption),
  })
  .superRefine(refineTaxIds);

export const inviteUserSchema = z.object({
  email,
  role: z.enum(ROLES, V.invalidOption),
  name: optionalText(120),
});

export const clearanceRuleSchema = z.object({
  system: trimmed(2, 120),
  gap: z.number(V.nonNegativeInt).int(V.nonNegativeInt).min(0).max(100, V.outOfRange),
  tol: z.number(V.nonNegativeInt).int(V.nonNegativeInt).min(0).max(200, V.outOfRange),
  diagTol: z.number(V.nonNegativeInt).int(V.nonNegativeInt).min(0).max(200, V.outOfRange),
  revealTol: z.number(V.nonNegativeInt).int(V.nonNegativeInt).min(0).max(500, V.outOfRange),
  floorGap: z.number().int().min(0).max(200).nullable().optional(),
  status: z.enum(CLEARANCE_RULE_STATUSES),
});
export type ClearanceRuleInput = z.input<typeof clearanceRuleSchema>;

// ---------------------------------------------------------------- projects / positions
export const projectCreateSchema = z.object({
  name: optionalText(160),
  clientId: z.uuid().optional(),
  clientName: trimmed(2, 160),
  clientPhone: phone,
  buildingType: z.enum(BUILDING_TYPES, V.invalidOption),
  address: trimmed(2, 200),
  city: trimmed(2, 100),
  surveyorId: z.uuid(V.required),
  dueDate: isoDate.optional(),
  note: optionalText(2000),
  clearanceRuleId: z.uuid().optional(),
  value: z.number().min(0, V.outOfRange).optional(),
});
export type ProjectCreateInput = z.input<typeof projectCreateSchema>;

/**
 * Novi projekat koji kreira mjerilac (`projects.create` bez `projects.write`), online ili offline:
 * bez `surveyorId` (server postavlja njega samog) i bez vrijednosti. Ista polja šalje sync `project` upsert.
 */
export const projectCreateFieldSchema = projectCreateSchema.omit({ surveyorId: true, value: true });
export type ProjectCreateFieldInput = z.input<typeof projectCreateFieldSchema>;

export const positionSpecSchema = z.object({
  productCategory: z.enum(['window', 'door', 'balcony_door', 'shutter']).nullable().optional(),
  product: z.string().max(120).nullable().optional(),
  combination: z.string().max(120).nullable().optional(),
  profile: z.string().max(120).nullable().optional(),
  glass: z.string().max(120).nullable().optional(),
  color: z.string().max(120).nullable().optional(),
  shutter: z.string().max(120).nullable().optional(),
  hardware: z.string().max(120).nullable().optional(),
});

export const positionInputSchema = z.object({
  id: z.uuid().optional(),
  label: z.string().trim().max(16, V.tooLong).optional(),
  room: z.string().trim().max(80, V.tooLong).nullable().optional(),
  openingType: z.enum(OPENING_TYPE_KEYS, V.invalidOption),
  qty: z.number().int(V.positiveInt).min(1, V.positiveInt).max(999, V.outOfRange).default(1),
  spec: positionSpecSchema.default({}),
  widthTop: mmSchema().optional(),
  widthMiddle: mmSchema().optional(),
  widthBottom: mmSchema().optional(),
  heightLeft: mmSchema().optional(),
  heightMiddle: mmSchema().optional(),
  heightRight: mmSchema().optional(),
  d1: mmSchema(30000).optional(),
  d2: mmSchema(30000).optional(),
  revealLeft: mm0Schema().optional(),
  revealRight: mm0Schema().optional(),
  wallThickness: mm0Schema().optional(),
  sillHeight: mm0Schema(5000).optional(),
  clearanceRuleId: z.uuid().nullable().optional(),
  note: z.string().max(2000, V.tooLong).nullable().optional(),
});
export type PositionInput = z.input<typeof positionInputSchema>;
export const positionPatchSchema = positionInputSchema.partial().omit({ id: true });

// ---------------------------------------------------------------- measurements / photos
export const decisionSchema = z
  .object({
    decision: z.enum(DECISIONS),
    note: z.string().trim().max(2000, V.tooLong).optional(),
    positionId: z.uuid().optional(),
  })
  .refine((v) => v.decision === 'approve' || (v.note !== undefined && v.note.length > 0), {
    path: ['note'],
    message: V.noteRequired,
  });

export const signatureSchema = z.object({
  role: z.enum(['surveyor', 'client']),
  svgPath: z.string().min(1, V.signatureRequired).max(200000),
  signerName: trimmed(2, 120),
  signedAt: z.iso.datetime(),
  device: z.string().max(120).nullable().optional(),
});

export const submitMeasurementSchema = z
  .object({
    signatures: z.array(signatureSchema).max(2, V.signatureRequired),
    consent: z.literal(true, V.consent),
    note: optionalText(2000),
  })
  .refine((v) => new Set(v.signatures.map((s) => s.role)).size === v.signatures.length, {
    path: ['signatures'],
    message: V.signatureRequired,
  });

export const photoMetaSchema = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  positionId: z.uuid().nullable().optional(),
  category: z.enum(PHOTO_CATEGORIES),
  takenAt: z.iso.datetime({ offset: true }),
  gpsLat: z.number().min(-90).max(90).nullable().optional(),
  gpsLng: z.number().min(-180).max(180).nullable().optional(),
  device: z.string().max(120).nullable().optional(),
  width: z.number().int().min(1).nullable().optional(),
  height: z.number().int().min(1).nullable().optional(),
});

export const photoTagInputSchema = z.object({
  id: z.uuid().optional(),
  xPct: z.number().min(0, V.outOfRange).max(100, V.outOfRange),
  yPct: z.number().min(0, V.outOfRange).max(100, V.outOfRange),
  title: trimmed(1, 120),
  note: z.string().max(1000, V.tooLong).nullable().optional(),
});

// ---------------------------------------------------------------- public
export const contactSchema = z.object({
  name: trimmed(2, 120),
  company: optionalText(160),
  email,
  phone,
  message: trimmed(10, 4000),
  honeypot: z.string().max(200).optional(),
  captchaToken: z.string().optional(),
});
export type ContactInput = z.input<typeof contactSchema>;

// ---------------------------------------------------------------- platforma i paketi
const nullableLimit = z
  .number(V.nonNegativeInt)
  .int(V.nonNegativeInt)
  .min(0, V.nonNegativeInt)
  .max(1_000_000, V.outOfRange)
  .nullable()
  .optional();
const price = z
  .number(V.nonNegativeInt)
  .min(0, V.outOfRange)
  .max(1_000_000, V.outOfRange)
  .nullable();

export const planLimitsSchema = z.object({
  users: nullableLimit,
  surveyors: nullableLimit,
  projectsPerMonth: nullableLimit,
  storageGb: z.number().min(0, V.outOfRange).max(100_000, V.outOfRange).nullable().optional(),
  devices: nullableLimit,
});

export const planSchema = z.object({
  code: z
    .string(V.required)
    .trim()
    .regex(/^[a-z0-9][a-z0-9_-]{1,39}$/, V.invalidOption),
  name: trimmed(2, 80),
  description: z.string().trim().max(1000, V.tooLong).nullable().optional(),
  priceMonthly: price.optional(),
  priceYearly: price.optional(),
  currency: z.enum(['BAM', 'EUR'], V.invalidOption),
  public: z.boolean().optional(),
  individual: z.boolean().optional(),
  sort: z.number().int().min(0).max(1000).optional(),
  features: z.partialRecord(z.enum(FEATURE_KEYS), z.enum(FEATURE_STATES)).optional(),
  limits: planLimitsSchema.optional(),
});
export type PlanFormInput = z.input<typeof planSchema>;

/** Razlog (odbijanje registracije, suspendovanje, impersonacija). */
export const reasonSchema = z.object({ reason: trimmed(5, 1000) });
export const impersonateSchema = z.object({ reason: trimmed(5, 500) });
export const planChangeRequestSchema = z.object({
  planCode: z.string().max(40).nullable().optional(),
  message: trimmed(3, 2000),
});
/** 6 cifara iz aplikacije za autentifikaciju ili rezervni kod (`xxxxx-xxxxx`). */
export const totpCodeSchema = z.object({
  code: z
    .string(V.required)
    .trim()
    .regex(/^([0-9]{6}|[a-z0-9]{5}-?[a-z0-9]{5})$/i, V.totp),
});
